import { SCREEN_NAME, SCREEN_VIEW, screenNameSchema } from '@/constants/analytics';
import { NavigationTrigger } from '@/constants/navigation';
import * as MegaMenu from '@/features/mega-menu/MegaMenu.types';
import { searchTrackingPropsSchema } from '@/features/searchV2/types/tracking';
import { z } from 'zod';

/**
 * @deprecated
 */
export type TriggerSource =
  | NavigationTrigger
  | (typeof SCREEN_NAME)[keyof typeof SCREEN_NAME]
  | (typeof SCREEN_VIEW)[keyof typeof SCREEN_VIEW];

const errorShownSchema = z.object({
  name: z.literal('error_shown'),
  properties: z.object({
    screen_name: screenNameSchema,
    error_type: z.string(),
    error_code: z.optional(z.string().regex(/^http_[0-9]{3}$/)),
  }),
});

const screenShownSchema = z.object({
  name: z.literal('screen_shown'),
  properties: z.object({
    screen_name: screenNameSchema,
  }),
});

const megaMenuClickSchema = z.object({
  name: z.literal('mega_menu_click'),
  properties: z.object({
    screen_name: screenNameSchema,
    mega_menu_tab: MegaMenu.tabIdentifierSchema,
    mega_menu_cta: z.string().nullable().optional(),
  }),
});

const companyClickedSchema = z.object({
  name: z.literal('company_clicked'),
  properties: z
    .object({
      screen_name: screenNameSchema,
      marketplace_id: z.number(),
      salon_id: z.number(),
      position: z.number(),
      number_of_ratings: z.string().optional(),
      ratings_average: z.number().optional(),
    })
    .merge(
      searchTrackingPropsSchema.pick({
        campaigns_shown: true,
        search_query: true,
        search_location_filter: true,
        search_location_city: true,
        nbr_search_results: true,
      }),
    ),
});

export const searchPerformedSchema = z.object({
  name: z.literal('search_performed'),
  properties: searchTrackingPropsSchema,
});

export const logoutFlowSchema = z.object({
  name: z.literal('logout_flow'),
  properties: z.object({
    logout_step: z.enum(['initiated', 'successful', 'cancelled']),
  }),
});

export const loginSignupFlowInitiatedFromSchema = z.enum([
  'navigation_bar',
  'navigation_bar_bookings',
  'navigation_bar_favorites',
  'recently_viewed',
  'place_page_favorited',
  'checkout_booking_summary',
  'checkout_booking_summary_force_login',
  'checkout_prior_booking_summary',
  'checkout_prior_bundle_summary',
  'support_form',
  'wellness_card_purchase',
  'footer',
  'footer_bookings',
  'footer_favorites',
  'save_giftcard_from_link',
  'direct_links',
  'profile_page_reviews',
  'finish_account',
]);

export type LoginSignupFlowInitiatedFrom = z.infer<typeof loginSignupFlowInitiatedFromSchema>;

export const loginSignupFlowSchema = z.object({
  name: z.literal('login_sign-up_flow'),
  properties: z.object({
    ['login_sign-up_session_id']: z.string().uuid(),
    initiated_from: loginSignupFlowInitiatedFromSchema,
    step: z.enum([
      'initiated',
      'cancelled',
      'continue_as_guest',
      'create_account',
      'verification',
      'terms_and_conditions',
      'continue_discovery',
      'booking_cancellation',
      'back_to_email_submission',
      'back_to_create_account',
      'resend_code',
      'correct_code',
      'code_submitted',
      'details_added',
      'add_details_later',
      'successfull',
      'failed',
      'wrong_code',
      'force_finish_sign_up',
      'prompted',
    ]),
    flow: z.enum(['login', 'sign-up']),
  }),
});

export declare namespace Tracking {
  export type ScreenName = z.infer<typeof screenNameSchema>;

  type ScreenShown = z.infer<typeof screenShownSchema>;

  type MegaMenuClick = z.infer<typeof megaMenuClickSchema>;

  type CompanyClicked = z.infer<typeof companyClickedSchema>;

  type SearchPerformed = z.infer<typeof searchPerformedSchema>;

  type ErrorShown = z.infer<typeof errorShownSchema>;

  type LogoutFlow = z.infer<typeof logoutFlowSchema>;

  type LoginSignupFlow = z.infer<typeof loginSignupFlowSchema>;

  type Event =
    | ScreenShown
    | CompanyClicked
    | MegaMenuClick
    | SearchPerformed
    | ErrorShown
    | LoginSignupFlow
    | LogoutFlow;
}
